import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { cn } from '../../lib/utils';
import {
  Phone, MessageSquareText, Mail, MessageCircle, Search, Send, User,
  Clock, CheckCircle2, ArrowRight, Bot, Paperclip, PhoneIncoming,
  PhoneOutgoing, PhoneMissed, Voicemail
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_CONVERSATIONS = [
  { id: 1, patient: 'Amanda Rodriguez', channel: 'phone', lastMsg: 'Called about insurance verification', time: '2 min ago', unread: true, status: 'active', agent: 'Front Desk Agent' },
  { id: 2, patient: 'John Smith', channel: 'sms', lastMsg: 'Yes, I confirm my appointment for tomorrow at 9 AM', time: '15 min ago', unread: false, status: 'resolved', agent: null },
  { id: 3, patient: 'Emily Davis', channel: 'email', lastMsg: 'RE: Appointment confirmation and pre-visit forms', time: '1 hr ago', unread: true, status: 'pending', agent: null },
  { id: 4, patient: 'Maria Garcia', channel: 'sms', lastMsg: 'Can I reschedule my Thursday appointment?', time: '2 hrs ago', unread: false, status: 'resolved', agent: 'Front Desk Agent' },
  { id: 5, patient: 'Robert Johnson', channel: 'phone', lastMsg: 'Left voicemail about prescription refill', time: '3 hrs ago', unread: true, status: 'pending', agent: null },
  { id: 6, patient: 'James Taylor', channel: 'chat', lastMsg: 'What time does the clinic open on Saturday?', time: '4 hrs ago', unread: false, status: 'resolved', agent: 'Front Desk Agent' },
];

const DEMO_MESSAGES = [
  { id: 1, from: 'patient', text: 'Hi, I need to verify my insurance before my appointment tomorrow.', time: '2:15 PM' },
  { id: 2, from: 'agent', text: 'Hello Amanda! I can help with that. I see your appointment is scheduled for tomorrow at 10:30 AM with Dr. Wilson. Let me check your insurance status.', time: '2:15 PM', agent: 'Front Desk Agent' },
  { id: 3, from: 'agent', text: 'I found your Blue Cross PPO plan (Member ID: BCB-987654321). Your coverage is active and the copay for an office visit is $30. Would you like me to send you a pre-visit checklist?', time: '2:16 PM', agent: 'Front Desk Agent' },
  { id: 4, from: 'patient', text: 'Yes please! Also, do I need to bring anything else?', time: '2:18 PM' },
  { id: 5, from: 'agent', text: "Great! I've sent the checklist to your email. Please bring your insurance card and a photo ID. If you have any recent lab results, please bring those too. See you tomorrow!", time: '2:18 PM', agent: 'Front Desk Agent' },
];

const DEMO_CALL_LOG = [
  { id: 1, patient: 'Amanda Rodriguez', type: 'incoming', duration: '3:24', time: '2:15 PM', status: 'answered', handler: 'Front Desk Agent' },
  { id: 2, patient: 'Robert Johnson', type: 'incoming', duration: '0:00', time: '11:30 AM', status: 'voicemail', handler: null },
  { id: 3, patient: 'Maria Garcia', type: 'outgoing', duration: '1:45', time: '10:15 AM', status: 'answered', handler: 'Follow-Up Agent' },
  { id: 4, patient: 'James Taylor', type: 'incoming', duration: '0:00', time: '9:42 AM', status: 'missed', handler: null },
  { id: 5, patient: 'Emily Davis', type: 'outgoing', duration: '2:10', time: '9:00 AM', status: 'answered', handler: 'Staff' },
];

const CHANNEL_ICON = { phone: Phone, sms: MessageSquareText, email: Mail, chat: MessageCircle };
const CALL_ICON = { incoming: PhoneIncoming, outgoing: PhoneOutgoing, missed: PhoneMissed, voicemail: Voicemail };

const EchoComms = () => {
  const { isDemo } = useAppMode();
  // Real mode must not expose synthetic call/message records. The backend
  // does not yet publish a comms stream, so in real mode we render the
  // page scaffolding with explicit empty states — no demo names leak.
  const conversations = isDemo ? DEMO_CONVERSATIONS : [];
  const messages = isDemo ? DEMO_MESSAGES : [];
  const callLog = isDemo ? DEMO_CALL_LOG : [];

  const [activeTab, setActiveTab] = useState('inbox');
  const [selectedConvo, setSelectedConvo] = useState(conversations[0] || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMsg, setNewMsg] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = conversations.filter(c => {
    if (filter !== 'all' && c.channel !== filter) return false;
    if (searchTerm && !c.patient.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const handleSend = () => {
    if (!newMsg.trim()) return;
    toast.success('Message sent', { description: `Reply sent to ${selectedConvo.patient}` });
    setNewMsg('');
  };

  return (
    <div className="space-y-6" data-testid="echo-comms-page">
      <div>
        <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Communication Hub</h1>
        <p className="text-slate-500 mt-1">All patient communications in one place</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Unread', value: conversations.filter(c => c.unread).length, icon: MessageSquareText, color: 'text-blue-600 bg-blue-50' },
          { label: 'Active Calls', value: isDemo ? 1 : 0, icon: Phone, color: 'text-emerald-600 bg-emerald-50' },
          { label: 'Pending', value: conversations.filter(c => c.status === 'pending').length, icon: Clock, color: 'text-amber-600 bg-amber-50' },
          { label: 'AI Handled', value: conversations.filter(c => c.agent).length, icon: Bot, color: 'text-violet-600 bg-violet-50' },
        ].map((s, i) => (
          <Card key={i} className="border-slate-200"><CardContent className="p-3 flex items-center gap-3">
            <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", s.color.split(' ')[1])}><s.icon className={cn("h-4 w-4", s.color.split(' ')[0])} /></div>
            <div><p className="text-lg font-bold">{s.value}</p><p className="text-[10px] text-muted-foreground">{s.label}</p></div>
          </CardContent></Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="inbox">Inbox</TabsTrigger>
          <TabsTrigger value="calls">Call Log</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[500px]">
            {/* Conversation List */}
            <Card className="border-slate-200 lg:col-span-1">
              <CardContent className="p-0">
                <div className="p-3 border-b border-slate-100">
                  <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" /><Input placeholder="Search..." className="pl-9 h-8 text-sm" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} /></div>
                  <div className="flex gap-1 mt-2">
                    {['all', 'phone', 'sms', 'email', 'chat'].map(f => (
                      <Button key={f} variant={filter === f ? 'default' : 'ghost'} size="sm" className={cn("h-6 px-2 text-[10px]", filter === f && "bg-black text-white")} onClick={() => setFilter(f)}>
                        {f === 'all' ? 'All' : f.toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="divide-y divide-slate-100 max-h-[440px] overflow-y-auto">
                  {filtered.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="inbox-empty">
                      <MessageSquareText className="h-7 w-7 text-slate-300 mb-2" />
                      <p className="text-xs text-slate-500">No conversations yet</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {isDemo ? 'No matches for your filter.' : 'Incoming calls, SMS and emails will appear here.'}
                      </p>
                    </div>
                  ) : filtered.map(c => {
                    const Icon = CHANNEL_ICON[c.channel];
                    return (
                      <div key={c.id} className={cn("flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors", selectedConvo?.id === c.id && "bg-slate-50")} onClick={() => setSelectedConvo(c)} data-testid={`convo-${c.id}`}>
                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><User className="h-4 w-4 text-slate-500" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between"><p className={cn("text-sm truncate", c.unread ? "font-semibold" : "font-medium")}>{c.patient}</p><p className="text-[10px] text-muted-foreground shrink-0">{c.time}</p></div>
                          <div className="flex items-center gap-1.5"><Icon className="h-3 w-3 text-slate-400 shrink-0" /><p className="text-xs text-muted-foreground truncate">{c.lastMsg}</p></div>
                        </div>
                        {c.unread && <div className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />}
                        {!c.unread && c.agent && <Badge className="bg-emerald-50 text-emerald-600 text-[8px] border-0 shrink-0">AI</Badge>}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Message Thread */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardContent className="p-0 flex flex-col h-full min-h-[500px]">
                {selectedConvo ? (
                  <>
                    <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center"><User className="h-4 w-4 text-slate-500" /></div>
                        <div><p className="text-sm font-semibold">{selectedConvo.patient}</p><p className="text-[10px] text-muted-foreground">{selectedConvo.channel.toUpperCase()}{selectedConvo.agent && ` · Handled by ${selectedConvo.agent}`}</p></div>
                      </div>
                      <Badge className={cn("text-[10px]", selectedConvo.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' : selectedConvo.status === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700')}>{selectedConvo.status}</Badge>
                    </div>
                    <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                      {messages.map(m => (
                        <div key={m.id} className={cn("flex", m.from === 'patient' ? "justify-start" : "justify-end")}>
                          <div className={cn("max-w-[75%] p-3 rounded-xl", m.from === 'patient' ? "bg-slate-100 rounded-tl-none" : "bg-black text-white rounded-tr-none")}>
                            {m.agent && <p className="text-[9px] opacity-60 mb-1 flex items-center gap-1"><Bot className="h-2.5 w-2.5" />{m.agent}</p>}
                            <p className="text-sm">{m.text}</p>
                            <p className={cn("text-[10px] mt-1", m.from === 'patient' ? "text-muted-foreground" : "text-white/50")}>{m.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 border-t border-slate-100 flex gap-2">
                      <Input placeholder="Type a reply..." className="flex-1" value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} data-testid="comms-input" />
                      <Button variant="ghost" size="icon"><Paperclip className="h-4 w-4" /></Button>
                      <Button onClick={handleSend} className="bg-black hover:bg-gray-800" size="icon" data-testid="comms-send"><Send className="h-4 w-4" /></Button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-center p-8">
                    <div><MessageSquareText className="h-8 w-8 text-slate-300 mx-auto mb-2" /><p className="text-sm text-muted-foreground">Select a conversation</p></div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="calls">
          <Card className="border-slate-200">
            <CardHeader className="pb-3"><CardTitle className="text-base font-semibold">Call Log</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {callLog.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center" data-testid="call-log-empty">
                    <Phone className="h-7 w-7 text-slate-300 mb-2" />
                    <p className="text-xs text-slate-500">No call history yet</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">Incoming and outgoing calls will be logged here.</p>
                  </div>
                ) : callLog.map(call => {
                  const Icon = CALL_ICON[call.type] || Phone;
                  return (
                    <div key={call.id} className="flex items-center gap-4 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" data-testid={`call-${call.id}`}>
                      <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center", call.status === 'answered' ? 'bg-emerald-50' : call.status === 'missed' ? 'bg-red-50' : 'bg-slate-100')}>
                        <Icon className={cn("h-4 w-4", call.status === 'answered' ? 'text-emerald-600' : call.status === 'missed' ? 'text-red-600' : 'text-slate-500')} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{call.patient}</p>
                        <p className="text-xs text-muted-foreground">{call.type} · {call.time} · {call.duration !== '0:00' ? call.duration : call.status}</p>
                      </div>
                      {call.handler && call.handler.includes('Agent') && <Badge className="bg-emerald-50 text-emerald-600 text-[9px] gap-1 border-0"><Bot className="h-2.5 w-2.5" />Handled by AI · {call.time}</Badge>}
                      {call.handler && !call.handler.includes('Agent') && <Badge variant="outline" className="text-[9px] gap-1"><Bot className="h-2.5 w-2.5" />{call.handler}</Badge>}
                      {!call.handler && <Badge variant="outline" className="text-[9px] text-slate-400">No handler</Badge>}
                      <Badge className={cn("text-[10px]", call.status === 'answered' ? 'bg-emerald-100 text-emerald-700' : call.status === 'missed' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600')}>{call.status}</Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EchoComms;
